<template>
	<div ref="section"
		 class="enlargeable-image"
		 :class="{ 'active': status != 'delarged' }">
		<div ref="orgImgContainer"
			 class="org-img-container reveal"
			 data-cursor-custom
			 data-cursor-text="ZOOM"
			 @click="e => enlargeImage(e)">
			<BasePicture v-if="data?.image"
						 class="org-img"
						 :image="data.image"
						 :alt="data.text"
						 :sizes="sizes"
						 :width="getAspectRatio.width"
						 :height="getAspectRatio.height"
						 :style="{ '--aspect-ratio': aspectRatio }"
						 loading="lazy" />
		</div>
		<client-only>
			<teleport :to="'#so-teleport-area'">

				<div ref="lightbox"
					 class="enlarged-lightbox"
					 :style="{ backgroundImage: `url(${backgroundUrl})` }"
					 @click="resetImage">

					<a ref="enlargedclose"
					   href="#"
					   class="close-section"
					   role="button"
					   title="close overlay"
					   data-cursor-custom
					   data-cursor-text="CLOSE"
					   data-text-instant
					   @click.prevent="reset">
						<closeIcon />
					</a>
					<BasePicture v-if="data?.image"
								 :id="uid"
								 ref="bigImage"
								 class="big-img"
								 :image="data.image"
								 :width="data.image.width"
								 :height="data.image.height"
								 :modifiers="{ withoutEnlargement: true }"
								 fit="cover" />
				</div>
			</teleport>
		</client-only>
	</div>
</template>

<script setup>
import closeIcon from '~/assets/images/icon-close.svg'

const props = defineProps({
	data: {
		type: [String, Object],
		required: true,
	},
	animationDuration: {
		type: String,
		default: '400'
	},
	sizes: {
		type: String,
		default: null
	},
	aspectRatio: {
		type: String,
		default: null
	}
})

const { gsap } = useGsap()
const { $bus } = useNuxtApp()
const config = useRuntimeConfig()
const metaStore = useMetaStore()

const lightboxRef = useTemplateRef('lightbox')
const enlargedcloseRef = useTemplateRef('enlargedclose')
const orgImgContainerRef = useTemplateRef('orgImgContainer')
const bigImageRef = useTemplateRef('bigImage')

const uid = useId()
const status = ref('delarged')
const backgroundUrl = ref('')
let enlargeTL

const getLargeSizes = (image) => {
	const breakpoints = ['xs', 'md', 'lg', 'xl', '2xl', '3xl']
	const isPortrait = image.height > image.width
	const size = isPortrait ? '100vh' : '100vw'

	const sizes = breakpoints.map(bp => `${bp}:${size}`).join(' ')
	return sizes.toString().trim()
}


const getAspectRatio = computed(() => {
	if (!props.aspectRatio) return { width: null, height: null }

	const [width, height] = props.aspectRatio.split('/').map(Number)

	if (isNaN(width) || isNaN(height) || height === 0 || !width || !height) {
		console.warn('Invalid aspect ratio format. Expected "width/height".')
		return { width: null, height: null }
	}

	return { width, height }
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function animEnlargeImage(e) {
	let closePos
	let el = document.getElementById('hamburger')
	const bgColor = getComputedStyle(lightboxRef.value).getPropertyValue('--background-color').trim()

	if (!el) el = document.getElementById('book-call-button')
	if (el) {
		closePos = calculatePosition(el)
		const closeRect = enlargedcloseRef.value.getBoundingClientRect()

		gsap.set(enlargedcloseRef.value, {
			left: closePos.right - (closeRect.width / 2),
			top: closePos.top + (closePos.height / 2),
		})
	}

	enlargeTL = gsap.timeline({
		paused: true,
		// reversed: true,
		onStart: () => { $bus.$emit('scroll-stop'); status.value = 'enlarging' },
		onReverseComplete: () => { $bus.$emit('scroll-start'); status.value = 'delarged' },
		onComplete: () => { status.value = 'enlarged' }
	})

	enlargeTL
		.set(lightboxRef.value, {
			autoAlpha: 1,
			...calculatePosition(orgImgContainerRef.value),
		})
		.to(orgImgContainerRef.value, {
			autoAlpha: 0,
			duration: 0.2,
			ease: 'power3.inOut'
		})
		.to(lightboxRef.value, {
			backgroundColor: bgColor,
			duration: 0.2,
			ease: 'power1.inOut'
		})
		.to(lightboxRef.value, {
			left: 0,
			top: 0,
			width: '100vw',
			height: '100vh',
			zIndex: 990,
			duration: 0.5,
			ease: 'power3.inOut'
		}, '<')
		.to(lightboxRef.value, {
			backgroundColor: metaStore.labelTypeColor,
			duration: 0.2,
			ease: 'power1.inOut'
		}, '-=0.2').to(enlargedcloseRef.value, {
			autoAlpha: 1,
			duration: 0.2
		}, '-=0.1')
		.to('#' + uid, {
			autoAlpha: 1,
			duration: 0.2
		})
}

function enlargeImage(e) {
	animEnlargeImage(e)
	enlargeTL.play()
}

function resetImage() {
	status.value = 'delarging'
	enlargeTL.timeScale(1.5).reverse()
}

//util functions
function calculatePosition(el) {
	const rect = el.getBoundingClientRect()

	return {
		top: Math.round(rect.top),
		left: Math.round(rect.left),
		right: Math.round(rect.right),
		height: rect.height,
		width: rect.width,
	}
}

function buildImageUrl(id, fmt = 'orig') {
	const base = config.public.mediaBase
	const fmtParam = fmt === 'orig' ? '' : `?format=${fmt}`
	return `${base}${id}${fmtParam}&withoutEnlargement=true&width=${windowWidth.value}`
}

const windowWidth = computed(() => {
	return window?.innerWidth
})

onMounted(async () => {
	backgroundUrl.value = buildImageUrl(props.data?.image?.id, 'webp').toString()
})
</script>

<style lang="scss" scoped>
.enlargeable-image {
	.org-img-container {
		display: inline-block;
		width: 100%;
		max-width: 100%;
		max-height: 100%;
		cursor: pointer;

		:deep(picture) {
			width: 100%;
			max-width: 100%;
			vertical-align: middle;

			img {
				width: 100%;
				height: auto;
				object-fit: cover;
				aspect-ratio: var(--aspect-ratio);
				@include rounded-corners;
			}
		}
	}

	&.active .org-img-container {
		opacity: 0;
	}
}

.enlarged-lightbox {
	display: flex;
	position: fixed;
	align-items: center;
	justify-content: center;
	background-position: center center;
	background-repeat: no-repeat;
	background-size: contain;
	visibility: hidden;
	cursor: pointer;
	width: 0;
	height: 0;
	// background-color: var(--background-color, #121212);
	background-color: transparent;
	transition: background-color 0.3s ease;
	overflow: hidden;

	.close-section {
		display: block;
		visibility: hidden;
		position: absolute;
		top: rem-calc(62);
		left: 95.8vw;
		transform: translate(-50%, -50%);
		color: var(--foreground-color, #121212);
		border: none;
		pointer-events: auto;
	}

	:deep(picture) {
		display: block;
		width: 100%;
		height: 100%;
		visibility: hidden;

		img {
			object-fit: contain;
			max-height: 100%;
			max-width: 100%;
			width: 100%;
			height: 100%;
		}
	}
}
</style>
